import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from 'react-flexbox-grid';
import { is, path, filter } from 'ramda';
import { Dialog, RaisedButton } from 'material-ui';
import { FormattedMessage } from 'react-intl';

import HistoryCollapseContainer from 'containers/HistoryCollapseContainer';
import HistoryEntry from 'components/HistoryEntry';
import HistoryEntryHeader from 'components/HistoryEntryHeader';
import HistoryEntryColumns from 'components/HistoryEntryColumns';
import HistoryEntryColumn from 'components/HistoryEntryColumn';
import Paper from 'components/Paper';

import EditableV2 from 'routes/Documents/containers/EditableV2';
import CommonHeader from 'routes/Documents/components/CommonHeader';
import CommonHistoryEntryControls from 'routes/Documents/components/CommonHistoryEntryControls';
import BottomFormControls from 'routes/Documents/components/BottomFormControls';
import CommonViewButton from 'routes/Documents/components/CommonViewButton';
import {
  getLatestFinishedCommitData,
  getLatestUnfinishedCommit,
  getLatestCommitDate,
  getFirstCommitDate,
} from 'lib/documentHandling';

import { formatHeaderDate, editTimeLeft, notOlderThanOneMonth } from 'lib/dateDisplay';

import StatusForm from './StatusForm';
import PREDSSForm from './PREDSSForm';
import MSNQForm from 'routes/Documents/routes/Scores/components/MSNQForm';
import FSMCForm from 'routes/Documents/routes/Scores/components/FSMCForm';
import calculateMSNQTotals from 'routes/Documents/routes/Scores/calculateMSNQ';
import calculateFSMCTotals from 'routes/Documents/routes/Scores/calculateFSMC';
import { getScoreSummaryDialog } from 'routes/Documents/routes/Scores/getScoreSummaryDialog';
import { sectionHeader } from './Status.less';
import normalize from 'lib/normalize';

import { historyViewFields, historyViewLegacyFields, questionnaireNames, isLegacyDocument } from '../Utils';
import exists from 'lib/exists';
import { vasWorse, vasBetter } from './Status.less';
import { dateFromPartial } from 'neuro-utils';

const highLightStyle = {
  border: '#048b82',
  backgroundColor: '#e2f1f0',
  padding: '0.5rem',
  marginTop: '0.5rem',
  borderRadius: '0rem',
  textAlign: 'center',
};

const newLinkStyle = {
  color: 'black',
  textDecoration: 'underline',
};

const sortDocs = documents =>
  documents.sort((d1, d2) => {
    const date1Stamp = d1?.content?.date ? dateFromPartial(d1.content.date).getTime() : d1.createDate;
    const date2Stamp = d2?.content?.date ? dateFromPartial(d2.content.date).getTime() : d2.createDate;
    return date2Stamp - date1Stamp;
  });
const sortCommits = commits => commits.sort((c1, c2) => c2.createDate - c1.createDate);
const getLatestCommitTime = docs => {
  const latestDoc = path([0], docs);
  if (latestDoc && latestDoc.commits && latestDoc.commits.length > 0) {
    return path([0, 'createDate'], sortCommits(latestDoc.commits));
  }
  return 0;
};

const handleHistoryValue = (text, value, additional = undefined, title = undefined, previousAdditional = undefined) =>
  is(Number, value) || is(Number, additional) ? (
    <React.Fragment>
      {/* <p title={title}>{value === 0 ? text : value}</p> */}
      {additional !== undefined && (
        <p
          className={
            additional > previousAdditional ? vasWorse : additional < previousAdditional ? vasBetter : undefined
          }
        >
          {is(Number, value) ? `(${additional})` : additional}
        </p>
      )}
    </React.Fragment>
  ) : null;

const historyMapper = (name, edit, view, create, optData) => (d, intl, i) => {
  const isLegacy = isLegacyDocument(d.content);

  // Previously filled document to compare values
  const prevDoc = path([i + 1], sortDocs(normalize(optData.allDocuments)));

  const printAddNew = nameDate => {
    return (
      d.documentId === path(['latestStatusId'], optData) &&
      getLatestCommitDate(d.commits) > path([nameDate], optData) &&
      notOlderThanOneMonth(d.createDate)
    );
  };

  const getNewQuestionnaireLink = columnName => {
    if (questionnaireNames[columnName]) {
      const qName = questionnaireNames[columnName];
      return (
        <div style={printAddNew(`${qName}Date`) ? highLightStyle : {}}>
          {printAddNew(`${qName}Date`) && (
            <a
              style={newLinkStyle}
              onClick={() => {
                create(qName);
              }}
            >
              {intl.formatMessage({ id: `status.new.${qName}` })}
            </a>
          )}
        </div>
      );
    }
    return '';
  };

  const getHistoryEntryColumn = columnName => {
    return (
      <HistoryEntryColumn
        header={intl.formatMessage({ id: `status.${columnName}.titleShort` })}
        smallScaling={4}
        key={columnName}
      >
        {exists(
          path(['sections', `${columnName}`], d.content) ||
            exists(path(['sections', `${columnName}VASValue`], d.content)),
        ) &&
          handleHistoryValue(
            intl.formatMessage({ id: 'status.opts.unimpairing' }),
            undefined, // path(['sections', columnName], d.content),
            path(['sections', `${columnName}VASValue`], d.content),
            path(['sections', columnName], d.content)
              ? intl.formatMessage({
                  id: `status.${columnName}.${path(['sections', columnName], d.content)}`,
                })
              : undefined,
            path(['content', 'sections', `${columnName}VASValue`], prevDoc),
          )}
        {getNewQuestionnaireLink(columnName)}
      </HistoryEntryColumn>
    );
  };

  const getHistoryEntryColumns = columns => {
    return <HistoryEntryColumns>{columns.map(col => getHistoryEntryColumn(col))}</HistoryEntryColumns>;
  };

  if (d.documentType === name && d.commits.length > 0) {
    // Print only main documents history
    const dateTimestamp = 'date' in d.content ? dateFromPartial(d.content.date).getTime() : d.createDate;
    return (
      <HistoryEntry key={d.documentId}>
        <HistoryEntryHeader
          title={formatHeaderDate(dateTimestamp)}
          active={editTimeLeft(getFirstCommitDate(d.commits))}
        >
          {!isLegacy && window.__ROLE__ !== 'DOCTOR' && editTimeLeft(getFirstCommitDate(d.commits)) && (
            <CommonHistoryEntryControls
              time={getFirstCommitDate(d.commits)}
              action={() => edit(name, d.documentId, getLatestFinishedCommitData(d))}
            />
          )}
          {!isLegacy && (window.__ROLE__ === 'DOCTOR' || !editTimeLeft(getFirstCommitDate(d.commits))) && (
            <CommonViewButton onView={e => view(name, d.documentId)} />
          )}
        </HistoryEntryHeader>
        {isLegacy ? getHistoryEntryColumns(historyViewLegacyFields) : getHistoryEntryColumns(historyViewFields)}
      </HistoryEntry>
    );
  }
};

const predssHistoryMapper = (name, edit, view) => (d, intl) => (
  <HistoryEntry key={d.documentId}>
    <HistoryEntryHeader title={formatHeaderDate(d.createDate)} active={editTimeLeft(d.createDate)}>
      {window.__ROLE__ !== 'DOCTOR' && editTimeLeft(d.createDate) && (
        <CommonHistoryEntryControls
          time={d.createDate}
          action={() => edit(name, d.documentId, getLatestFinishedCommitData(d))}
        />
      )}
      {(window.__ROLE__ === 'DOCTOR' || !editTimeLeft(d.createDate)) && (
        <CommonViewButton onView={() => view('predss', d.documentId)} />
      )}
    </HistoryEntryHeader>
    <HistoryEntryColumns>
      {exists(path(['sections', 'prEDSS'], d.content)) && (
        <HistoryEntryColumn
          header={intl.formatMessage({ id: `status.prEDSS.titleShort` })}
          smallScaling={4}
          key="prEDSS"
        >
          {handleHistoryValue(
            intl.formatMessage({ id: 'status.opts.unimpairing' }),
            path(['sections', 'prEDSS'], d.content),
          )}
        </HistoryEntryColumn>
      )}
    </HistoryEntryColumns>
  </HistoryEntry>
);

class Status extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      scoreDialogOpen: false,
      scoreDialogContent: undefined,
    };
  }

  setDialog = (doc, type) => () => {
    this.setState(getScoreSummaryDialog(doc, type, this.props.documents));
  };
  closeDialog = () => {
    this.setState({ scoreDialogOpen: false, scoreDialogContent: undefined });
  };
  dialogActions = [
    <RaisedButton label={<FormattedMessage id="infoDialog.close" />} primary onClick={this.closeDialog} />,
  ];

  componentDidUpdate(prevProps) {
    const prevEditDoc = prevProps.editingDocument;
    const editDocNow = prevEditDoc
      ? filter(d => d.documentId === prevEditDoc.documentId, this.props.documents)[0]
      : this.props.documents[0];

    // True if document was saved
    const saved =
      editDocNow &&
      editDocNow.commits.length > 0 &&
      getLatestCommitDate(editDocNow.commits) &&
      getLatestCommitDate(editDocNow.commits) !==
        ((prevEditDoc && prevEditDoc.commits.length > 0 && getLatestCommitDate(prevEditDoc.commits)) || 0);

    if (prevProps.editingDocument && !this.props.editingDocument && saved) {
      const docType = path(['documentType'], prevProps.editingDocument);
      if (docType === 'msnq') {
        this.setDialog(normalize(calculateMSNQTotals([prevProps.editingDocument]))[0], 'msnq')();
      } else if (docType === 'fsmc') {
        this.setDialog(calculateFSMCTotals([prevProps.editingDocument])[0], 'fsmc')();
      }
    }
  }

  render() {
    const { names, documents, editingDocument, viewId, onView, onStartEdit, onCreate } = this.props;
    return (
      <div>
        <Paper topMargin={false}>
          <Grid fluid>
            <CommonHeader
              names={['status', 'predss']}
              localeId={
                path(['documentType'], editingDocument) === 'msnq'
                  ? 'msnq.title'
                  : path(['documentType'], editingDocument) === 'fsmc'
                    ? 'fsmc.title'
                    : path(['documentType'], editingDocument) === 'status'
                      ? 'status.header'
                      : path(['documentType'], editingDocument) === 'predss'
                        ? 'predss.title'
                        : 'status.title'
              }
              infoText={
                path(['documentType'], editingDocument) === 'msnq'
                  ? 'scores.description.MSNQ'
                  : path(['documentType'], editingDocument) === 'fsmc'
                    ? 'scores.description.FSMC'
                    : path(['documentType'], editingDocument) === 'status'
                      ? 'status.infoTextStatus'
                      : path(['documentType'], editingDocument) === 'predss'
                        ? 'status.infoTextPredss'
                        : 'status.infoText'
              }
              mode="create"
              editing={editingDocument}
              startEdit={onCreate}
              widerInfoTextArea
            />
          </Grid>
          {editingDocument && (
            <EditableV2
              documentId={editingDocument.documentId}
              moduleName={editingDocument.documentType}
              editedCommit={getLatestUnfinishedCommit(editingDocument)}
            >
              {{
                status: <StatusForm />,
                predss: <PREDSSForm />,
                msnq: <MSNQForm />,
                fsmc: <FSMCForm />,
              }[editingDocument.documentType] || <></>}
            </EditableV2>
          )}

          {!editingDocument && viewId && (
            <React.Fragment>
              {{
                status: (
                  <StatusForm
                    editing={false}
                    formValues={getLatestFinishedCommitData(documents.find(d => d.documentId === viewId.id))}
                  />
                ),
                predss: (
                  <PREDSSForm
                    editing={false}
                    formValues={getLatestFinishedCommitData(documents.find(d => d.documentId === viewId.id))}
                  />
                ),
              }[viewId.name] || <></>}
            </React.Fragment>
          )}
          {!editingDocument && !viewId && (
            <React.Fragment>
              <h4 className={sectionHeader}>
                <FormattedMessage id="status.header" />
              </h4>
              <HistoryCollapseContainer
                documents={sortDocs(documents.filter(d => d.documentType === 'status'))}
                emptyText="status.emptyText"
                sorter={sortDocs}
                mapper={historyMapper(names[0], onStartEdit, onView, onCreate, {
                  msnqDate: getLatestCommitTime(sortDocs(documents.filter(d => d.documentType === 'msnq'))),
                  fsmcDate: getLatestCommitTime(sortDocs(documents.filter(d => d.documentType === 'fsmc'))),
                  latestStatusId: path([0, 'documentId'], sortDocs(documents.filter(d => d.documentType === 'status'))),
                  allDocuments: documents,
                })}
                limit={2}
              />
              <h4 className={sectionHeader}>
                <FormattedMessage id="status.prEDSS.header" />
              </h4>
              <HistoryCollapseContainer
                documents={sortDocs(documents.filter(d => d.documentType === 'predss'))}
                emptyText="status.emptyText"
                sorter={sortDocs}
                mapper={predssHistoryMapper('predss', onStartEdit, onView)}
                limit={2}
              />
            </React.Fragment>
          )}
        </Paper>
        {editingDocument && (
          <BottomFormControls
            name={editingDocument.documentType}
            editId={editingDocument.documentId}
            commitId={getLatestUnfinishedCommit(editingDocument).commitId}
          />
        )}
        {
          <Dialog
            open={this.state.scoreDialogOpen}
            onRequestClose={this.closeDialog}
            actions={this.dialogActions}
            autoScrollBodyContent
          >
            {this.state.scoreDialogContent}
          </Dialog>
        }
      </div>
    );
  }
}

Status.propTypes = {
  names: PropTypes.array,
  documents: PropTypes.array,
  onStartEdit: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onView: PropTypes.func,
  viewId: PropTypes.object,
  editingDocument: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
};

export default Status;
